/* This module is specific to printer selection(s) within the backordering / PO system.
   It enables a dual selection of confirmation and PO printers.  For screen prints,
   it enables combining the two into one ScreenPrintModal display after backorder submission.

   Standalone sublet POs and unpriced part POs (PRO1/SMM3) are supported via the standalonePO={true} prop.

   Per the model defintion, confirmations should share the same printers as quotes,
   with a fallback to the appropriate invoice printers defined if no quote printers are confgiured.
   PO printers are defined explitcly with no fallback mechanism.

   Confirmation prints have the following options:
   mode: Y/2 (Y = normal print, 2 = 2 copies)

   PO prints have the following options (per PO):
   mode: Y/2/E/EP/EP2 (Y = normal print, 2 = 2 copies, E = email, EP = email and print, EP2 = duh)
   Remove Customer Info
   Combine Parts
   Print By Unit Pack

   This modal works on the "lastBackorderData" property of the model.  Thus, it can be used
   automatically post-submission, or interactively via a popup, e.g. "Print Last Backorder Documents".
*/

import _ from 'lodash';
import React, { FunctionComponent, useRef, useState } from 'react';
import { Button, Modal, FormCheck } from 'react-bootstrap';
import classNames from 'classnames';
import { ClipLoader } from 'react-spinners';
import Model, { IBOPrintOptions, IScreenPrint } from '../../model/ShopKeeper.model';

/* Unlike the generalized PrinterSelectionModal, activePrinterDefaults, printerLists, and
   confirmationScreenPrinterIDs are all specific to the backordering process here, and can be extracted
   directly from the model.
*/
interface IProps {
  container: Object;
  hide: () => void;
  model: Model;
  show: boolean;
  standalonePO?: boolean; // 1 or more POs
}

interface IPrinterDetails {
  id: string;
  name: string;
}

const PrintSelectionBOModal: FunctionComponent<IProps> = ({ container, hide, model, show, standalonePO = false }) => {
  // If the confirmation modal is currently displayed, this is a confirmation reprint request.
  const confirmationReprint = model.showConfirmationModal;

  // "NOW" Backorders also optionally print the attached purchase orders
  const includePurchaseOrders = standalonePO || (!confirmationReprint && model.lastBackorderData.boType === 'NOW');

  const sp = model.sysParams;
  const [confirmationPrinterList] = useState<IPrinterDetails[]>(
    sp.confirmationQuotePrinters.length > 0 ? sp.confirmationQuotePrinters : sp.invoicePrinters // possibly [], no further fallback
  );
  const [confirmationScreenPrinterID] = useState(
    sp.confirmationQuotePrinters.length > 0 && sp.screenPrinters.confirmationQuote !== null
      ? sp.screenPrinters.confirmationQuote
      : sp.screenPrinters.invoice // possibly null, no further fallback
  );
  const [poPrinterList] = useState<IPrinterDetails[]>(sp.poPrinters); // possibly [], no fallback
  const [poScreenPrinterID] = useState(sp.screenPrinters.po); // possibly null

  /* Confirm any printer selection saved in local storage still exists in the current
     priner lists.  Legacy may have changed ids/name, in which case the default selection
     of "Do Not Print" will be chosen.
  */
  const [confirmationActivePrinter, setActiveConfirmationPrinter] = useState<IPrinterDetails>(() => {
    const lastSelected = JSON.parse(localStorage.getItem('blazeConfirmationPrinter')!);
    if (lastSelected) {
      if (~_.findIndex(confirmationPrinterList, { id: lastSelected.id, name: lastSelected.name })) {
        return lastSelected;
      }
    }
    return {
      id: '',
      name: '',
    };
  });
  const [poActivePrinter, setActivePoPrinter] = useState<IPrinterDetails>(() => {
    const lastSelected = JSON.parse(localStorage.getItem('blazePoPrinter')!);
    if (lastSelected) {
      if (~_.findIndex(poPrinterList, { id: lastSelected.id, name: lastSelected.name })) {
        return lastSelected;
      }
    }
    return {
      id: '',
      name: '',
    };
  });

  const [printOptions, setPrintOptions] = useState<IBOPrintOptions>(() => {
    let savedPrintOptions;
    const blazeBOprintOptions = localStorage.getItem('blazeBOprintOptions');
    if (blazeBOprintOptions) {
      try {
        savedPrintOptions = JSON.parse(blazeBOprintOptions);
      } catch (e: any) {
        console.error(e.message);
      }
    }
    return {
      combineItemsOnPOs: _.get(savedPrintOptions, 'combineItemsOnPOs', false),
      emailPOsToVendors: _.get(savedPrintOptions, 'emailPOsToVendors', false),
      includePOComments: _.get(savedPrintOptions, 'includePOComments', false),
      printByUnitPackOnPOs: _.get(savedPrintOptions, 'printByUnitPackOnPOs', false),
      printTwoConfirmationCopies: _.get(savedPrintOptions, 'printTwoConfirmationCopies', false),
      printTwoPurchaseOrderCopies: _.get(savedPrintOptions, 'printTwoPurchaseOrderCopies', false),
      removeCustomerInfoFromPOs: _.get(savedPrintOptions, 'removeCustomerInfoFromPOs', false),
    };
  });

  const [isProcessing, setIsProcessing] = useState(false);

  const processButton = useRef<HTMLButtonElement>(null);

  const handleOptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = e.target;
    setPrintOptions({ ...printOptions, [name]: checked });
  };

  // In all print cases, the model is aware of the last backorder run's result.
  // The model need only be informed of printer selection.
  const process = async () => {
    // Save printer selections for next run (only save PO printer selection on NOW backorders)
    localStorage.setItem('blazeConfirmationPrinter', JSON.stringify(confirmationActivePrinter));
    if (includePurchaseOrders) {
      localStorage.setItem('blazePoPrinter', JSON.stringify(poActivePrinter));
    }

    // Save print options for next run (PO options will be resaved but unchanged on DAILY backorders)
    localStorage.setItem('blazeBOprintOptions', JSON.stringify(printOptions));

    const confirmationPrinterSelected = confirmationActivePrinter.id !== '';
    const confirmationScreenPrint = confirmationActivePrinter.id === confirmationScreenPrinterID;
    const poPrinterSelected = poActivePrinter.id !== '';
    const poScreenPrint = poActivePrinter.id === poScreenPrinterID;

    // Screen prints on either doctype will cause a Processing spinner to be shown
    // on the printer selector modal while the documents are prepared by Blaze/Omniconv
    if (confirmationScreenPrint || (includePurchaseOrders && poScreenPrint)) {
      const screenPrintDocs: IScreenPrint[] = [];
      setIsProcessing(true);

      if (!standalonePO && confirmationPrinterSelected) {
        if (confirmationScreenPrint) {
          screenPrintDocs.push(...(await model.printConfirmation(confirmationActivePrinter.id, printOptions, true)));
        } else {
          await model.printConfirmation(confirmationActivePrinter.id, printOptions, false);
        }
      }

      if (includePurchaseOrders && poPrinterSelected) {
        if (poScreenPrint) {
          screenPrintDocs.push(
            ...(await model.printPurchaseOrders(poActivePrinter.id, printOptions, true, standalonePO))
          );
        } else {
          await model.printPurchaseOrders(poActivePrinter.id, printOptions, false, standalonePO);
        }
      }

      hide();

      if (screenPrintDocs.length > 0) {
        model.screenPrintDocs = screenPrintDocs;
        model.showScreenPrintModal = true;
      }

      // Non-screen prints on both doctypes close the print modal immediately and fire off
      // the print requests to the model in the background (though still in serial).
    } else {
      hide();
      if (!standalonePO && confirmationPrinterSelected) {
        await model.printConfirmation(confirmationActivePrinter.id, printOptions, false);
      }
      if (includePurchaseOrders && poPrinterSelected) {
        await model.printPurchaseOrders(poActivePrinter.id, printOptions, false, standalonePO);
      }
    }

    /* The following edgecase hits outside of the logic above by deduction, and is okay to perform post hide().
       A user may wish to e-mail POs to vendors but NOT print them (including a screen print). This case enables that.
       Printer selection is required in this case, even though it's ignored by Phil's routines and nothing actually prints.
    */
    if (includePurchaseOrders && !poPrinterSelected && poPrinterList.length > 0 && printOptions.emailPOsToVendors) {
      await model.printPurchaseOrders(
        poPrinterList[0].id,
        { ...printOptions, emailOnlyNoPrints: true },
        false,
        standalonePO
      );
    }
  };

  return (
    <Modal
      size="lg"
      backdrop="static"
      container={container as any}
      dialogClassName="sk-printerSelectionModal"
      onHide={hide as any}
      show={show}
    >
      <Modal.Body>
        <div className="sk-backorderDetails">
          {standalonePO ? (
            <>
              <strong>Purchase Orders:</strong>{' '}
              {model.lastStandalonePOData.poNumbers.map(poNumber => (
                <span key={poNumber}>{poNumber} &nbsp;</span>
              ))}
            </>
          ) : confirmationReprint ? (
            <>
              <strong>Confirmaton Number:</strong> {model.activeConfirmation!.details.confirmationNumber}
            </>
          ) : (
            <>
              <strong>Confirmation Number:</strong> {model.lastBackorderData.confirmationNumber} &nbsp; &nbsp;
              <strong>Purchase Orders:</strong>{' '}
              {model.lastBackorderData.poNumbers.map(poNumber => (
                <span key={poNumber}>{poNumber} &nbsp;</span>
              ))}
            </>
          )}
        </div>
        <div className="sk-confirmationPoPrinters">
          {!standalonePO && (
            <div className="sk-printerTypeSelector" style={{ width: includePurchaseOrders ? '50%' : '100%' }}>
              {confirmationPrinterList.length === 0 && (
                <span style={{ color: 'red', fontWeight: 'bold' }}>No confirmation printers defined.</span>
              )}
              {confirmationPrinterList.length !== 0 && (
                <>
                  <label>
                    <strong>Confirmation Printer Selection:</strong>{' '}
                    {confirmationActivePrinter.id !== '' ? (
                      <span style={{ color: 'green', fontWeight: 'bold' }}>{confirmationActivePrinter.name}</span>
                    ) : (
                      'Not Selected'
                    )}
                  </label>
                  <div className="sk-printerTableContainer">
                    <table className="table sk-printerTable">
                      <tbody>
                        <tr
                          className={classNames({
                            'sk-printerSelected': confirmationActivePrinter.id === '',
                            'sk-selectorRow': true,
                          })}
                          onClick={() => setActiveConfirmationPrinter({ id: '', name: '' })}
                        >
                          <td className="sk-printerType">
                            <i className="zmdi zmdi-block-alt" />
                          </td>
                          <td className="sk-printerName">Do Not Print</td>
                        </tr>
                        {/* Show screen printer first */}
                        {confirmationScreenPrinterID && (
                          <tr
                            className={classNames({
                              'sk-printerSelected': confirmationScreenPrinterID === confirmationActivePrinter.id,
                              'sk-selectorRow': true,
                            })}
                            onClick={() =>
                              setActiveConfirmationPrinter({ id: confirmationScreenPrinterID, name: 'Screen' })
                            }
                          >
                            <td className="sk-printerType">
                              <i className="zmdi zmdi-tv" />
                            </td>
                            <td className="sk-printerName">Print to Screen</td>
                          </tr>
                        )}
                        {/* Followed by all other non-screen printers (CUPS) */}
                        {confirmationPrinterList.map((printer, index) => {
                          if (printer.id === confirmationScreenPrinterID) {
                            return null;
                          }

                          return (
                            <tr
                              key={`printer-${index}`}
                              className={classNames({
                                'sk-printerSelected': printer.id === confirmationActivePrinter.id,
                                'sk-selectorRow': true,
                              })}
                              onClick={() => setActiveConfirmationPrinter({ id: printer.id, name: printer.name })}
                            >
                              <td className="sk-printerType">
                                <i className="zmdi zmdi-print" />
                              </td>
                              <td className="sk-printerName">{printer.name}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
          {includePurchaseOrders && (
            <div className="sk-printerTypeSelector" style={{ width: standalonePO ? '100%' : '50%' }}>
              {poPrinterList.length === 0 && (
                <span style={{ color: 'red', fontWeight: 'bold' }}>No PO printers defined.</span>
              )}
              {poPrinterList.length !== 0 && (
                <>
                  <label>
                    <strong>PO Printer Selection:</strong>{' '}
                    {poActivePrinter.id !== '' ? (
                      <span style={{ color: 'green', fontWeight: 'bold' }}>{poActivePrinter.name}</span>
                    ) : (
                      'Not Selected'
                    )}
                  </label>
                  <div className="sk-printerTableContainer">
                    <table className="table sk-printerTable">
                      <tbody>
                        <tr
                          className={classNames({
                            'sk-printerSelected': poActivePrinter.id === '',
                            'sk-selectorRow': true,
                          })}
                          onClick={() => setActivePoPrinter({ id: '', name: '' })}
                        >
                          <td className="sk-printerType">
                            <i className="zmdi zmdi-block-alt" />
                          </td>
                          <td className="sk-printerName">Do Not Print</td>
                        </tr>
                        {/* Show screen printer first */}
                        {poScreenPrinterID && (
                          <tr
                            className={classNames({
                              'sk-printerSelected': poScreenPrinterID === poActivePrinter.id,
                              'sk-selectorRow': true,
                            })}
                            onClick={() => setActivePoPrinter({ id: poScreenPrinterID, name: 'Screen' })}
                          >
                            <td className="sk-printerType">
                              <i className="zmdi zmdi-tv" />
                            </td>
                            <td className="sk-printerName">Print to Screen</td>
                          </tr>
                        )}
                        {/* Followed by all other non-screen printers (CUPS) */}
                        {poPrinterList.map((printer, index) => {
                          if (printer.id === poScreenPrinterID) {
                            return null;
                          }

                          return (
                            <tr
                              key={`printer-${index}`}
                              className={classNames({
                                'sk-printerSelected': printer.id === poActivePrinter.id,
                                'sk-selectorRow': true,
                              })}
                              onClick={() => setActivePoPrinter({ id: printer.id, name: printer.name })}
                            >
                              <td className="sk-printerType">
                                <i className="zmdi zmdi-print" />
                              </td>
                              <td className="sk-printerName">{printer.name}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <div className="sk-boPrintOptions">
          {!standalonePO && (
            <div className="sk-printOptionSelectors">
              {confirmationPrinterList.length !== 0 && (
                <FormCheck>
                  <FormCheck.Input
                    type="checkbox"
                    id="printTwoConfirmationCopies"
                    name="printTwoConfirmationCopies"
                    className="sk-useMaterialInput"
                    onChange={handleOptionChange}
                    checked={printOptions.printTwoConfirmationCopies}
                  />
                  <FormCheck.Label htmlFor="printTwoConfirmationCopies">Print 2 copies of confirmation</FormCheck.Label>
                </FormCheck>
              )}
            </div>
          )}
          {includePurchaseOrders && (
            <div className="sk-printOptionSelectors">
              {poPrinterList.length !== 0 && (
                <>
                  <FormCheck>
                    <FormCheck.Input
                      type="checkbox"
                      id="printTwoPurchaseOrderCopies"
                      name="printTwoPurchaseOrderCopies"
                      className="sk-useMaterialInput"
                      onChange={handleOptionChange}
                      checked={printOptions.printTwoPurchaseOrderCopies}
                    />
                    <FormCheck.Label htmlFor="printTwoPurchaseOrderCopies">
                      Print 2 copies of each purchase order
                    </FormCheck.Label>
                  </FormCheck>
                  <FormCheck>
                    <FormCheck.Input
                      type="checkbox"
                      id="includePOComments"
                      name="includePOComments"
                      className="sk-useMaterialInput"
                      onChange={handleOptionChange}
                      checked={printOptions.includePOComments}
                    />
                    <FormCheck.Label htmlFor="includePOComments">Include PO Comments</FormCheck.Label>
                  </FormCheck>
                  <FormCheck>
                    <FormCheck.Input
                      type="checkbox"
                      id="removeCustomerInfoFromPOs"
                      name="removeCustomerInfoFromPOs"
                      className="sk-useMaterialInput"
                      onChange={handleOptionChange}
                      checked={printOptions.removeCustomerInfoFromPOs}
                    />
                    <FormCheck.Label htmlFor="removeCustomerInfoFromPOs">
                      Remove customer info from purchase orders
                    </FormCheck.Label>
                  </FormCheck>
                  <FormCheck>
                    <FormCheck.Input
                      type="checkbox"
                      id="combineItemsOnPOs"
                      name="combineItemsOnPOs"
                      className="sk-useMaterialInput"
                      onChange={handleOptionChange}
                      checked={printOptions.combineItemsOnPOs}
                    />
                    <FormCheck.Label htmlFor="combineItemsOnPOs">Combine items on purchase orders</FormCheck.Label>
                  </FormCheck>
                  <FormCheck>
                    <FormCheck.Input
                      type="checkbox"
                      id="printByUnitPackOnPOs"
                      name="printByUnitPackOnPOs"
                      className="sk-useMaterialInput"
                      onChange={handleOptionChange}
                      checked={printOptions.printByUnitPackOnPOs}
                    />
                    <FormCheck.Label htmlFor="printByUnitPackOnPOs">
                      Print by unit pack on purchase orders
                    </FormCheck.Label>
                  </FormCheck>
                  <FormCheck>
                    <FormCheck.Input
                      type="checkbox"
                      id="emailPOsToVendors"
                      name="emailPOsToVendors"
                      className="sk-useMaterialInput"
                      onChange={handleOptionChange}
                      checked={printOptions.emailPOsToVendors}
                    />
                    <FormCheck.Label htmlFor="emailPOsToVendors">E-mail purchase orders to vendors</FormCheck.Label>
                  </FormCheck>
                </>
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={isProcessing} onClick={() => hide()} variant="secondary">
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={() => {
            if (!isProcessing) {
              process();
            }
          }}
          ref={processButton}
        >
          {isProcessing ? (
            <>
              <ClipLoader size={13} color="white" /> PROCESSING
            </>
          ) : (
            'PROCESS'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PrintSelectionBOModal;
